import type { PlatformContract } from "@ondit/publishing";
import { Button, ChipModal, ChipModalBody, ChipModalHeader, Input } from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useAccess } from "~/core/access";
import { ApiError, api } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import { apiUrl } from "~/core/api/url";
import { keys } from "~/core/query";
import "./connect.css";
export function ConnectAccount({
	entityId,
	platform,
	disabled,
	size,
	variant = "ghost",
	onCreated,
	accountId,
	taskJobId,
}: {
	entityId: string;
	platform: Pick<PlatformContract, "id" | "name" | "credential_provider" | "credential_kind">;
	disabled?: boolean;
	size?: "sm";
	variant?: "ghost" | "outline";
	onCreated?: (id: string) => void;
	accountId?: string;
	taskJobId?: string;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [loginRequest, setLoginRequest] = useState(() => crypto.randomUUID());
	const [created, setCreated] = useState<string | null>(accountId ?? null);
	const [job, setJob] = useState(taskJobId ?? "");
	const [receipt, setReceipt] = useState("");
	const [controlled, setControlled] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const frameRef = useRef<HTMLIFrameElement>(null);
	const [frameReady, setFrameReady] = useState(false);
	const [frameFailed, setFrameFailed] = useState(false);
	const [frameRevision, setFrameRevision] = useState(0);
	useEffect(() => {
		setFrameReady(false);
		setFrameFailed(false);
		if (!open || !controlled || !job) return;
		let timer: number;
		const waitForFrame = () => {
			window.clearTimeout(timer);
			setFrameReady(false);
			setFrameFailed(false);
			timer = window.setTimeout(() => setFrameFailed(true), 15000);
		};
		waitForFrame();
		const receive = (event: MessageEvent) => {
			if (
				event.source !== frameRef.current?.contentWindow ||
				event.origin !== new URL(apiUrl("/api/v1"), window.location.href).origin
			)
				return;
			if (event.data?.type === "pubzu-browser-frame") {
				window.clearTimeout(timer);
				setFrameReady(true);
				setFrameFailed(false);
			} else if (event.data?.type === "pubzu-browser-status" && event.data.status === "Waiting for handoff") {
				waitForFrame();
			}
		};
		window.addEventListener("message", receive);

		return () => {
			window.removeEventListener("message", receive);
			window.clearTimeout(timer);
		};
	}, [open, controlled, job, frameRevision]);
	const connection = useQuery({
		queryKey: [entityId, "channel-login", job],
		queryFn: () => api.connectionStatus(entityId, job),
		enabled: open && !!job,
		refetchInterval: 2000,
		retry: false,
	});
	useEffect(() => {
		if (
			connection.data?.connected ||
			(taskJobId && ["published", "unknown", "failed", "cancelled"].includes(connection.data?.state ?? ""))
		) {
			setOpen(false);
			if (!taskJobId) setJob("");
			setControlled(false);
			if (!accountId) setCreated(null);
			void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
		}
	}, [connection.data?.connected, connection.data?.state, entityId, queryClient, accountId, taskJobId]);
	useEffect(() => {
		if (!open || !job || controlled || verifying || !connection.data?.needs_login) return;
		let disposed = false;
		void api
			.connectionControl(entityId, job)
			.then(() => {
				if (!disposed) setControlled(true);
			})
			.catch(() => {
				if (!disposed)
					setError(pick(locale, ["Login session is unavailable. Try again.", "登录会话暂不可用，请重试。"]));
			});
		return () => {
			disposed = true;
		};
	}, [open, job, controlled, verifying, connection.data?.needs_login, entityId, locale]);
	const capabilities = useQuery({
		queryKey: [entityId, "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
	});
	const loginAvailable =
		capabilities.data?.account_capabilities?.some(
			(channel) => channel.platform_id === platform.id && channel.login_available === true,
		) === true;
	const beginConnection = async () => {
		if (busy || !loginAvailable) return;
		setOpen(true);
		setBusy(true);
		setError("");
		try {
			const id = created ?? (await api.createPubzuAccount(entityId, platform.id, "", loginRequest)).id;
			setCreated(id);
			onCreated?.(id);
			const login = await api.loginPubzuAccount(entityId, id, loginRequest);
			setJob(login.id);
			setControlled(false);
			await queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
			setLoginRequest(crypto.randomUUID());
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<>
			<Button
				variant={variant}
				size={size}
				disabled={disabled || !editor || busy || (!taskJobId && !loginAvailable)}
				title={
					!taskJobId && !loginAvailable
						? pick(locale, ["Account connection is not available for this channel yet.", "此渠道暂未开放账号连接。"])
						: undefined
				}
				onClick={() => (job ? setOpen(true) : void beginConnection())}
			>
				{taskJobId
					? pick(locale, ["Continue distribution", "继续分发"])
					: !loginAvailable
						? pick(locale, ["Connection unavailable", "暂不可连接"])
						: accountId
							? pick(locale, ["Reconnect", "重新连接"])
							: t("publishing.platform.connectAccount")}
			</Button>
			<ChipModal
				size="md"
				fullScreen
				open={open}
				onOpenChange={setOpen}
				srTitle={t("publishing.platform.connectAccount")}
			>
				<ChipModalHeader
					className="shrink-0"
					onClose={() => setOpen(false)}
					closeAriaLabel={t("common.close")}
					actions={
						job ? (
							<Button
								disabled={!connection.data?.needs_login || busy || !controlled}
								onClick={() =>
									void api
										.connectionResume(
											entityId,
											job,
											connection.data?.guided ? { confirmed: true, receipt_url: receipt || undefined } : undefined,
										)
										.then(async () => {
											setControlled(false);
											setVerifying(true);
											await connection.refetch();
											setVerifying(false);
										})
										.catch(() => setError(pick(locale, ["Unable to verify login.", "暂时无法确认登录。"])))
								}
							>
								{taskJobId
									? pick(locale, ["Confirm submission", "确认已提交"])
									: pick(locale, ["Confirm connection", "确认连接"])}
							</Button>
						) : undefined
					}
				>
					{platform.name}
				</ChipModalHeader>
				<ChipModalBody className="channel-login-body">
					{job ? (
						<div className="channel-login-session">
							<p className="hint">
								{pick(locale, [
									taskJobId
										? "Complete submission below, then confirm the result."
										: "Complete sign-in below, then confirm to verify the connection.",
									taskJobId ? "请在下方完成提交，再确认结果。" : "请在下方完成登录，完成后点击确认连接。",
								])}
							</p>
							{taskJobId && connection.data?.guided && (
								<div className="channel-guidance">
									<p>
										{pick(locale, [
											"Submit the prepared content in the browser. Without a public receipt, the result stays unconfirmed.",
											"请在浏览器中提交已准备的内容；未提供公开回执时，结果将保留待核验。",
										])}
									</p>
									<Input
										aria-label={pick(locale, ["Public receipt URL", "公开回执链接"])}
										placeholder={pick(locale, ["Public receipt URL (optional)", "公开回执链接（选填）"])}
										value={receipt}
										onChange={(e) => setReceipt(e.target.value)}
									/>
									{connection.data.fields?.map((field) => (
										<details key={field.key}>
											<summary>{pick(locale, field.label)}</summary>
											<pre>{field.value}</pre>
										</details>
									))}
								</div>
							)}
							{controlled && connection.data?.needs_login ? (
								<div className="channel-login-viewport">
									<iframe
										ref={frameRef}
										key={frameRevision}
										title={pick(locale, ["Channel sign-in", "渠道登录"])}
										src={apiUrl(`/api/v1/entities/${entityId}/platform-publishing/connections/${job}/browser`)}
										referrerPolicy="no-referrer"
										onError={() => setFrameFailed(true)}
									/>
									{!frameReady && (
										<div className="channel-login-frame-status" role="status">
											<p>
												{frameFailed
													? pick(locale, [
															"No browser frame received. Reconnect to try again.",
															"未收到浏览器画面，请重新连接。",
														])
													: pick(locale, ["Connecting to the browser…", "正在连接浏览器画面…"])}
											</p>
											{frameFailed && (
												<Button variant="outline" onClick={() => setFrameRevision((value) => value + 1)}>
													{pick(locale, ["Reconnect browser", "重新连接画面"])}
												</Button>
											)}
										</div>
									)}
								</div>
							) : (
								<p role="status">{pick(locale, ["Preparing or verifying login…", "正在准备或确认登录…"])}</p>
							)}
							{(error || connection.isError) && (
								<p role="alert">
									{error || pick(locale, ["Unable to read connection status.", "暂时无法获取连接状态。"])}
								</p>
							)}
							{["failed", "cancelled", "unknown"].includes(connection.data?.state ?? "") && (
								<p role="alert">
									{pick(locale, [
										"Connection was not completed. Start a new login session.",
										"连接未完成，请重新启动登录会话。",
									])}
								</p>
							)}
							{["failed", "cancelled", "unknown"].includes(connection.data?.state ?? "") && (
								<Button
									variant="outline"
									onClick={() => {
										if (!taskJobId) setJob("");
										setControlled(false);
										setVerifying(false);
										void beginConnection();
									}}
								>
									{pick(locale, ["Restart connection", "重新连接"])}
								</Button>
							)}
						</div>
					) : (
						<div className="channel-login-session">
							{error ? (
								<>
									<p role="alert">{error}</p>
									<Button variant="outline" disabled={busy} onClick={() => void beginConnection()}>
										{pick(locale, ["Retry connection", "重新连接"])}
									</Button>
								</>
							) : (
								<p role="status">{pick(locale, ["Preparing login…", "正在准备登录…"])}</p>
							)}
						</div>
					)}
				</ChipModalBody>
			</ChipModal>
		</>
	);
}
